import { AbstractControl } from '@angular/forms';

export const UNEXPECTED_ERROR = 'Ha ocurrido un error inesperado';

/**
 * Sin respuesta (estado 0: red, CORS, servidor caído) o un 5xx: no hay nada que
 * el usuario pueda corregir, y el cuerpo, si lo hay, es técnico.
 */
export function isUnexpectedError(error: any): boolean {
    const status = error?.status;

    return status === 0 || (typeof status === 'number' && status >= 500);
}

/**
 * Los mensajes por campo de un 422 de validación
 * (`{"error": {"business_type": ["…"]}, "code": 422}`), o null si el error no
 * es de esa forma.
 */
export function apiFieldErrors(error: any): Record<string, string[]> | null {
    if (error?.status !== 422) {
        return null;
    }

    const payload = error?.error?.error ?? error?.error?.errors;

    if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
        return null;
    }

    const fields: Record<string, string[]> = {};

    for (const [field, value] of Object.entries(payload)) {
        const messages = fieldMessages({ [field]: value });

        if (messages.length > 0) {
            fields[field] = messages;
        }
    }

    return Object.keys(fields).length > 0 ? fields : null;
}

/** Lo que un formulario enseña tras un envío fallido. */
export interface FormErrorFeedback {
    /** Para la alerta junto al botón de envío. */
    message: string;
    /** Todos los mensajes de un 422 por campo, para el resumen de arriba; vacío si no lo es. */
    summary: string[];
}

/**
 * Tras un envío fallido: un 422 con errores por campo los pinta en su control
 * (error `api`, que lee `FieldErrorComponent`) y los devuelve para el resumen;
 * cualquier otro error, su mensaje legible, y el genérico solo con un 5xx o sin
 * red.
 *
 * Hay que llamarla DESPUÉS de rehabilitar el formulario: enable() recalcula la
 * validez y borraría el error de cada control. El error `api` se va solo en
 * cuanto el usuario cambia el valor, por la misma razón.
 *
 * Un campo que el formulario no tiene (o que tiene deshabilitado) solo sale en
 * el resumen; si no cae ninguno en un control, no hay campos que señalar y el
 * aviso es el propio mensaje de la API. `aliases` lleva a su control los campos de la API que el
 * formulario reúne en uno (`north` y `west` → `coordinates`).
 */
export function handleFormError(
    form: AbstractControl | null | undefined,
    error: any,
    aliases: Record<string, string> = {},
): FormErrorFeedback {
    const fields = apiFieldErrors(error);

    if (!fields) {
        return { message: apiErrorMessage(error), summary: [] };
    }

    const byControl: Record<string, string[]> = {};

    for (const [field, messages] of Object.entries(fields)) {
        const name = aliases[field] ?? field;
        byControl[name] = [...(byControl[name] ?? []), ...messages];
    }

    let marked = 0;

    for (const [name, messages] of Object.entries(byControl)) {
        const control = form?.get(name);

        if (control && control.enabled) {
            control.setErrors({ ...(control.errors ?? {}), api: fieldMessages({ [name]: messages }).join(' ') });
            control.markAsTouched();
            marked++;
        }
    }

    const summary = fieldMessages(fields);

    if (marked === 0) {
        return { message: summary.join(' '), summary: [] };
    }

    return {
        message: summary.length === 1
            ? 'El servidor ha rechazado un dato: revisa el campo marcado en rojo.'
            : `El servidor ha rechazado ${summary.length} datos: revisa los campos marcados en rojo.`,
        summary,
    };
}

/**
 * El mensaje legible de un error de la API.
 *
 * La API manda los errores bajo `error` con dos formas: un texto
 * (`{"error": "Este documento ya se importó", "code": 422}`) o, en los de
 * validación, un objeto con los mensajes de cada campo
 * (`{"error": {"file": ["El archivo no es un PDF válido"]}, "code": 422}`).
 * Esta función aplana los dos, para que nunca llegue un `[object Object]` a un
 * snackbar.
 */
export function apiErrorMessage(error: any, fallback = UNEXPECTED_ERROR): string {
    if (isUnexpectedError(error)) {
        return fallback;
    }

    const body = error?.error;
    const payload = body?.error ?? body?.errors;

    if (typeof payload === 'string' && payload.trim() !== '') {
        return payload;
    }

    if (payload && typeof payload === 'object') {
        const messages = fieldMessages(payload);

        if (messages.length > 0) {
            return messages.join(' ');
        }
    }

    if (typeof body?.message === 'string' && body.message.trim() !== '') {
        return body.message;
    }

    // El `message` de un HttpErrorResponse es técnico («Http failure response
    // for …»): solo se usa el de un error que no viene de la red.
    if (error?.status === undefined && typeof error?.message === 'string' && error.message.trim() !== '') {
        return error.message;
    }

    return fallback;
}

/**
 * Los mensajes por campo de un error de validación, en orden y sin repetir.
 */
export function fieldMessages(errors: Record<string, unknown>): string[] {
    const messages: string[] = [];

    for (const value of Object.values(errors)) {
        const list = Array.isArray(value) ? value : [value];

        for (const message of list) {
            if (typeof message === 'string' && message.trim() !== '' && !messages.includes(message)) {
                messages.push(message);
            }
        }
    }

    return messages;
}
